import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/hooks/useAuthStore'
import { useNotificationStore } from '@/hooks/useNotificationStore'
import AppShell from '@/components/layout/AppShell'
import RequireAuth from '@/components/common/RequireAuth'
import PWAInstallPrompt from '@/components/common/PWAInstallPrompt'
import ErrorBoundary from '@/components/common/ErrorBoundary'

// Lazy-loaded pages
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const AgentPage = lazy(() => import('@/pages/AgentPage'))
const BriefingPage = lazy(() => import('@/pages/BriefingPage'))
const TopListPage = lazy(() => import('@/pages/TopListPage'))
const SignalsPage = lazy(() => import('@/pages/SignalsPage'))
const SignalDetailPage = lazy(() => import('@/pages/SignalDetailPage'))
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))

function LoadingFallback() {
  return (
    <div className="min-h-dvh flex flex-col items-center justify-center gap-4 bg-bg animate-fade-in">
      <div
        style={{ width: 64, height: 64, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        className="bg-primary/10"
      >
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
          <polyline points="16 7 22 7 22 13" />
        </svg>
      </div>
      <div className="w-7 h-7 border-3 border-primary/30 border-t-primary rounded-full animate-spin" />
    </div>
  )
}

export default function App() {
  const { checkAuth, isAuthenticated } = useAuthStore()
  const connectStream = useNotificationStore((s) => s.connectStream)
  const disconnectStream = useNotificationStore((s) => s.disconnectStream)
  const fetchUnreadCount = useNotificationStore((s) => s.fetchUnreadCount)

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // 登录状态变化时，启/停 SSE
  useEffect(() => {
    if (isAuthenticated) {
      void fetchUnreadCount()
      connectStream()
    } else {
      disconnectStream()
    }
    return () => disconnectStream()
  }, [isAuthenticated, connectStream, disconnectStream, fetchUnreadCount])

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<RequireAuth><><PWAInstallPrompt /><AppShell /></></RequireAuth>}>
            <Route path="/" element={<AgentPage />} />
            <Route path="/agent" element={<Navigate to="/" replace />} />
            <Route path="/briefing" element={<BriefingPage />} />
            <Route path="/toplist" element={<TopListPage />} />
            <Route path="/signals" element={<SignalsPage />} />
            <Route path="/signals/:taskId" element={<SignalDetailPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/me" element={<ProfilePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}
